import React, { useState } from 'react';
import { isSafeHttpUrl } from './slackKeyboardShortcuts';

/**
 * Enhanced, lightweight GFM-ish renderer for Jira markdown / descriptions.
 * Supports:
 * - Inline: bold (**), italic (*), strikethrough (~~), code (`), links ([text](url))
 * - Blocks: Headings (# - ######), Tasklists (- [ ], - [x]), Bullet/Numbered lists,
 *   Blockquotes (>), Tables (|...|), Fenced code blocks (```), Horizontal rules (---)
 */

export function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re =
    /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|~~[^~]+~~)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('~~') && token.endsWith('~~')) {
      nodes.push(<s key={key}>{token.slice(2, -2)}</s>);
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(
        <code
          key={key}
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.92em',
            background: 'var(--z-fill-2)',
            padding: '1px 5px',
            borderRadius: 4,
          }}
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch && isSafeHttpUrl(linkMatch[2])) {
        nodes.push(
          <a
            key={key}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--z-accent, #3b82f6)', textDecoration: 'underline' }}
          >
            {linkMatch[1]}
          </a>,
        );
      } else if (linkMatch) {
        nodes.push(linkMatch[1]);
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(token);
    }
    last = match.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function isListLine(line: string): boolean {
  return /^(\s*)([-*]|\d+\.)\s+/.test(line);
}

function isTaskListLine(line: string): boolean {
  return /^(\s*)([-*]|\d+\.)\s+\[([ xX])\]\s+/.test(line);
}

function isQuoteLine(line: string): boolean {
  return /^\s*>\s?/.test(line);
}

function isHorizontalRule(block: string): boolean {
  return /^(\s*[-*_]\s*){3,}$/.test(block.trim());
}

function isTableBlock(block: string): boolean {
  const lines = block.trim().split('\n');
  return (
    lines.length >= 2 &&
    lines[0].includes('|') &&
    /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(lines[1])
  );
}

function renderParagraphLines(lines: string[], blockIndex: number): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  lines.forEach((line, li) => {
    if (li > 0) {
      nodes.push(<br key={`b-${blockIndex}-br-${li}`} />);
    }
    nodes.push(...renderInline(line, `p${blockIndex}l${li}`));
  });
  return nodes;
}

function renderListBlock(lines: string[], bi: number): React.ReactNode {
  const ordered = /^\s*\d+\.\s+/.test(lines.find((l) => l.trim()) ?? '');
  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag key={`b-${bi}`} style={{ margin: '0 0 10px', paddingLeft: 20 }}>
      {lines
        .filter((l) => l.trim())
        .map((line, li) => {
          const isTask = isTaskListLine(line);
          if (isTask) {
            const taskMatch = /^(\s*)([-*]|\d+\.)\s+\[([ xX])\]\s+(.*)$/.exec(line);
            const isChecked = taskMatch ? taskMatch[3].toLowerCase() === 'x' : false;
            const textContent = taskMatch ? taskMatch[4] : line;
            return (
              <li
                key={`b-${bi}-l-${li}`}
                style={{
                  listStyleType: 'none',
                  marginLeft: -16,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 3,
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  readOnly
                  aria-label={textContent}
                  style={{
                    margin: 0,
                    cursor: 'default',
                    accentColor: 'var(--z-accent, #3b82f6)',
                  }}
                />
                <span style={{ textDecoration: isChecked ? 'line-through' : 'none', opacity: isChecked ? 0.75 : 1 }}>
                  {renderInline(textContent, `b${bi}l${li}`)}
                </span>
              </li>
            );
          }

          return (
            <li key={`b-${bi}-l-${li}`} style={{ marginBottom: 3 }}>
              {renderInline(line.replace(/^\s*([-*]|\d+\.)\s+/, ''), `b${bi}l${li}`)}
            </li>
          );
        })}
    </ListTag>
  );
}

function renderHeadingBlock(block: string, bi: number): React.ReactNode {
  const lines = block.split('\n');
  const firstLine = lines[0];
  const levelMatch = /^(#{1,6})\s+/.exec(firstLine);
  const level = levelMatch ? levelMatch[1].length : 1;
  const heading = firstLine.replace(/^#{1,6}\s+/, '');

  const fontSizes = ['1.35em', '1.2em', '1.08em', '1em', '0.92em', '0.85em'];
  const fontSize = fontSizes[level - 1] || '1em';

  const headingNode = (
    <div
      key={`b-${bi}-h`}
      role="heading"
      aria-level={level}
      style={{
        margin: '12px 0 6px',
        fontSize,
        fontWeight: level <= 2 ? 700 : 600,
        color: 'var(--z-text)',
        letterSpacing: '-0.015em',
      }}
    >
      {renderInline(heading, `h${bi}`)}
    </div>
  );

  if (lines.length > 1) {
    const remainingLines = lines.slice(1);
    return (
      <React.Fragment key={`b-${bi}`}>
        {headingNode}
        <p style={{ margin: '0 0 10px', textWrap: 'pretty' }}>
          {renderParagraphLines(remainingLines, bi)}
        </p>
      </React.Fragment>
    );
  }

  return headingNode;
}

function renderBlockquote(lines: string[], bi: number): React.ReactNode {
  const cleaned = lines.map((l) => l.replace(/^\s*>\s?/, ''));
  return (
    <blockquote
      key={`b-${bi}`}
      style={{
        margin: '8px 0 10px',
        padding: '6px 14px',
        borderLeft: '3px solid var(--z-accent, #3b82f6)',
        background: 'var(--z-fill-2)',
        borderRadius: '0 8px 8px 0',
        color: 'var(--z-text-2)',
      }}
    >
      {renderParagraphLines(cleaned, bi)}
    </blockquote>
  );
}

function renderTableBlock(block: string, bi: number): React.ReactNode {
  const lines = block.trim().split('\n').filter((l) => l.trim());
  if (lines.length < 2) return null;

  const parseRow = (line: string) =>
    line
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());

  const headers = parseRow(lines[0]);
  const rows = lines.slice(2).map(parseRow);

  return (
    <div
      key={`b-${bi}`}
      style={{
        overflowX: 'auto',
        margin: '10px 0 14px',
        borderRadius: 8,
        border: '1px solid var(--z-fill-2)',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12.5,
          textAlign: 'left',
        }}
      >
        <thead>
          <tr style={{ background: 'var(--z-surface-subtle)' }}>
            {headers.map((h, hi) => (
              <th
                key={`th-${hi}`}
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: 'var(--z-text)',
                  borderBottom: '1px solid var(--z-fill-2)',
                }}
              >
                {renderInline(h, `tb-h-${hi}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={`tr-${ri}`}
              style={{
                borderBottom: ri < rows.length - 1 ? '1px solid var(--z-fill-2)' : 'none',
                background: ri % 2 === 1 ? 'var(--z-plate)' : 'transparent',
              }}
            >
              {row.map((cell, ci) => (
                <td
                  key={`td-${ri}-${ci}`}
                  style={{
                    padding: '8px 12px',
                    color: 'var(--z-text-3)',
                  }}
                >
                  {renderInline(cell, `tb-c-${ri}-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlockRenderer({ code, bi }: { code: string; bi: number }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          // Ignore clipboard permission errors in restricted environments
        });
    }
  };

  return (
    <div
      key={`b-${bi}`}
      style={{
        position: 'relative',
        margin: '10px 0 12px',
        borderRadius: 10,
        background: 'var(--z-fill-2)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={handleCopy}
        title="Copiar código"
        aria-label="Copiar código"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '3px 8px',
          fontSize: 11,
          borderRadius: 6,
          border: 'none',
          cursor: 'pointer',
          background: 'var(--z-surface)',
          color: 'var(--z-muted-2)',
          zIndex: 2,
        }}
      >
        {copied ? 'Copiado ✓' : 'Copiar'}
      </button>
      <pre
        style={{
          margin: 0,
          padding: '12px 14px',
          overflow: 'auto',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--z-text)',
        }}
      >
        {code}
      </pre>
    </div>
  );
}

export const BasicMarkdown: React.FC<{ text: string; style?: React.CSSProperties }> = ({
  text,
  style,
}) => {
  if (!text) return null;
  const blocks = text.replace(/\r\n/g, '\n').trim().split(/\n{2,}/);

  return (
    <div
      data-testid="basic-markdown"
      style={{
        margin: 0,
        fontSize: 13,
        lineHeight: 1.55,
        color: 'var(--z-muted-3)',
        whiteSpace: 'normal',
        ...style,
      }}
    >
      {blocks.map((block, bi) => {
        if (isHorizontalRule(block)) {
          return (
            <hr
              key={`b-${bi}`}
              style={{
                border: 'none',
                borderTop: '1px solid var(--z-fill-2)',
                margin: '14px 0',
              }}
            />
          );
        }

        if (isTableBlock(block)) {
          return renderTableBlock(block, bi);
        }

        const lines = block.split('\n');

        if (lines.every((line) => isQuoteLine(line) || !line.trim())) {
          return renderBlockquote(lines, bi);
        }

        if (lines.every((line) => isListLine(line) || !line.trim())) {
          return renderListBlock(lines, bi);
        }

        if (/^#{1,6}\s+/.test(block)) {
          return renderHeadingBlock(block, bi);
        }

        if (block.startsWith('```')) {
          const code = block.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
          return <CodeBlockRenderer key={`b-${bi}`} code={code} bi={bi} />;
        }

        return (
          <p key={`b-${bi}`} style={{ margin: '0 0 10px', textWrap: 'pretty' }}>
            {renderParagraphLines(lines, bi)}
          </p>
        );
      })}
    </div>
  );
};
